import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Camera as CameraIcon, RotateCcw, Check, X, Image as ImageIcon, RotateCw, AlertTriangle, ArrowLeft, Eye } from 'lucide-react';

// Lightweight, dependency-free document scanner:
//   Phase 1  capture  → live camera + "Capture" / "Choose from gallery" / fallback file input
//   Phase 2  crop     → user drags/resizes a crop rectangle, can rotate 90°, pick a mode
//                       ("Color" / "Enhanced" / "B&W"); enhancement is applied to a small
//                       preview in real time so the user sees the actual result
//   Phase 3  review   → shows the FINAL processed image at full modal size, "View full size"
//                       opens it in a new tab, "Upload" emits the File
//
// Explicitly does NOT touch OpenCV.js or jscanify — the 9 MB WASM init on the
// main thread caused a hard freeze in earlier versions.

const randomSuffix = () => Math.random().toString(36).slice(2, 10);
const buildFileName = (docName) => {
  const safe = String(docName || 'document').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'document';
  return `scan-${safe}-${randomSuffix()}.jpg`;
};

const fileToImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image-decode-failed')); };
  img.src = url;
});

const rotate90 = (source) => {
  const c = document.createElement('canvas');
  c.width = source.height; c.height = source.width;
  const ctx = c.getContext('2d');
  ctx.translate(c.width, 0);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(source, 0, 0);
  return c;
};

const toCanvas = (drawable, w, h) => {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d').drawImage(drawable, 0, 0);
  return c;
};

// ─────────── Pixel-level enhancement ───────────
// The "photo" look comes from wide dynamic range + JPEG grain. Real scanners
// clamp the paper to pure white and darken the ink; we approximate that with
// a percentile-based white-point stretch (5th/95th percentiles → 0/255).
const applyEnhanced = (ctx, w, h) => {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const hist = new Uint32Array(256);
  const total = w * h;
  // First pass: build luminance histogram (Rec. 601).
  for (let i = 0; i < d.length; i += 4) {
    const y = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) | 0;
    hist[y]++;
  }
  const lo = Math.round(total * 0.05);
  const hi = Math.round(total * 0.95);
  let cum = 0, black = 0, white = 255;
  for (let i = 0; i < 256; i++) { cum += hist[i]; if (cum >= lo) { black = i; break; } }
  cum = 0;
  for (let i = 0; i < 256; i++) { cum += hist[i]; if (cum >= hi) { white = i; break; } }
  if (white <= black) { black = 0; white = 255; }
  const scale = 255 / (white - black);
  // LUT for speed. Gentle S-curve after stretch to darken ink and lift paper.
  const lut = new Uint8ClampedArray(256);
  for (let i = 0; i < 256; i++) {
    const v0 = (i - black) * scale;
    // S-curve: pull mid-shadows down (contrast), keep highlights near white.
    const v1 = v0 < 128 ? v0 * 0.85 : 128 + (v0 - 128) * 1.15;
    lut[i] = Math.max(0, Math.min(255, v1));
  }
  for (let i = 0; i < d.length; i += 4) {
    const y = lut[(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) | 0];
    d[i] = y; d[i + 1] = y; d[i + 2] = y;   // grayscale
  }
  ctx.putImageData(img, 0, 0);
};

// Otsu global threshold — cheap, robust, and good on well-lit paper. If the
// input is very uneven, adaptive would help; keeping this dependency-free.
const applyBW = (ctx, w, h) => {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const total = w * h;
  const hist = new Uint32Array(256);
  const lumas = new Uint8Array(total);
  for (let i = 0, j = 0; i < d.length; i += 4, j++) {
    const y = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) | 0;
    lumas[j] = y; hist[y]++;
  }
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, wF, maxVar = 0, thresh = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t]; if (wB === 0) continue;
    wF = total - wB; if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const v = wB * wF * (mB - mF) * (mB - mF);
    if (v > maxVar) { maxVar = v; thresh = t; }
  }
  for (let i = 0, j = 0; i < d.length; i += 4, j++) {
    const v = lumas[j] > thresh ? 255 : 0;
    d[i] = v; d[i + 1] = v; d[i + 2] = v;
  }
  ctx.putImageData(img, 0, 0);
};

// Render a `sourceCanvas` sub-rect into a fresh canvas with the chosen mode
// applied at the pixel level. Called from both the live preview overlay and
// the final upload path — same code, same result.
const renderProcessed = (sourceCanvas, cropN, mode) => {
  const sw = sourceCanvas.width, sh = sourceCanvas.height;
  const cx = Math.max(0, Math.round(cropN.x * sw));
  const cy = Math.max(0, Math.round(cropN.y * sh));
  const cw = Math.max(1, Math.round(cropN.w * sw));
  const ch = Math.max(1, Math.round(cropN.h * sh));
  const out = document.createElement('canvas');
  out.width = cw; out.height = ch;
  const ctx = out.getContext('2d');
  ctx.drawImage(sourceCanvas, cx, cy, cw, ch, 0, 0, cw, ch);
  if (mode === 'enhanced') applyEnhanced(ctx, cw, ch);
  else if (mode === 'bw')  applyBW(ctx, cw, ch);
  return out;
};

const MODES = [
  { key: 'color',    label: 'Color' },
  { key: 'enhanced', label: 'Enhanced' },
  { key: 'bw',       label: 'B&W' },
];

export default function DocumentScanner({ open, docName, onCancel, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const galleryInputRef = useRef(null);
  const cropContainerRef = useRef(null);
  const previewImgRef = useRef(null);       // small dataURL preview inside the crop rectangle
  const reviewImgRef = useRef(null);        // full-size processed image on the review step

  const [phase, setPhase] = useState('capture');   // capture | crop | review
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraErr, setCameraErr] = useState('');
  const [sourceCanvas, setSourceCanvas] = useState(null);
  const [mode, setMode] = useState('enhanced');    // Enhanced is the default look
  const [crop, setCrop] = useState({ x: 0.05, y: 0.05, w: 0.9, h: 0.9 });
  const [reviewDataUrl, setReviewDataUrl] = useState('');
  const reviewCanvasRef = useRef(null);            // holds the *actual* pixels we'll upload

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
      streamRef.current = null;
    }
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch (_) { /* noop */ } }
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraErr('');
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraErr('Camera not available in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } }, audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (!v) { stream.getTracks().forEach((t) => t.stop()); return; }
      v.srcObject = stream;
      v.setAttribute('playsinline', 'true');
      await v.play().catch(() => {});
      setCameraReady(true);
    } catch (e) {
      setCameraErr(e?.name === 'NotAllowedError'
        ? 'Camera permission denied. Use "Choose from gallery" or the fallback below.'
        : 'Camera unavailable. Use "Choose from gallery" or the fallback below.');
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setPhase('capture');
    setSourceCanvas(null);
    setMode('enhanced');
    setCrop({ x: 0.05, y: 0.05, w: 0.9, h: 0.9 });
    setReviewDataUrl('');
    startCamera();
    return () => { stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const captureFromVideo = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    stopCamera();
    setSourceCanvas(c);
    setCrop({ x: 0.05, y: 0.05, w: 0.9, h: 0.9 });
    setPhase('crop');
  };

  const acceptImageFile = async (file) => {
    if (!file) return;
    try {
      const img = await fileToImage(file);
      const c = toCanvas(img, img.naturalWidth || img.width, img.naturalHeight || img.height);
      stopCamera();
      setSourceCanvas(c);
      setCrop({ x: 0.05, y: 0.05, w: 0.9, h: 0.9 });
      setPhase('crop');
    } catch (_) {
      setCameraErr('Could not read that image. Try another file.');
    }
  };

  const onGalleryPick = (ev) => {
    const f = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (f) acceptImageFile(f);
  };

  const rotate = () => {
    if (!sourceCanvas) return;
    setSourceCanvas(rotate90(sourceCanvas));
    setCrop({ x: 0.05, y: 0.05, w: 0.9, h: 0.9 });
  };

  const backToCapture = () => {
    setSourceCanvas(null);
    setReviewDataUrl('');
    setPhase('capture');
    startCamera();
  };

  const goToReview = () => {
    if (!sourceCanvas) return;
    const out = renderProcessed(sourceCanvas, crop, mode);
    reviewCanvasRef.current = out;
    setReviewDataUrl(out.toDataURL('image/jpeg', 0.9));
    setPhase('review');
  };

  const backToCrop = () => {
    setPhase('crop');
  };

  const upload = () => {
    const out = reviewCanvasRef.current;
    if (!out) return;
    out.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], buildFileName(docName), { type: 'image/jpeg' });
      onCapture(file);
    }, 'image/jpeg', 0.9);
  };

  const viewFullSize = () => {
    if (!reviewDataUrl) return;
    // Open in a new tab so the user can pinch/zoom natively.
    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (w) {
      w.document.write(`<title>Scan preview</title><body style="margin:0;background:#111;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${reviewDataUrl}" style="max-width:100%;height:auto;background:#fff"/></body>`);
    }
  };

  // ─────────── Live preview of the enhancement on the small crop image ───────────
  // We downscale a copy of the source once, then re-apply the pixel filter
  // whenever `mode` or `crop` changes — this keeps the preview snappy even on
  // a 12 MP photo.
  const previewSourceRef = useRef(null);
  useEffect(() => {
    if (!sourceCanvas) { previewSourceRef.current = null; return; }
    const maxSide = 900;
    const s = Math.min(1, maxSide / Math.max(sourceCanvas.width, sourceCanvas.height));
    const pw = Math.max(1, Math.round(sourceCanvas.width * s));
    const ph = Math.max(1, Math.round(sourceCanvas.height * s));
    const c = document.createElement('canvas');
    c.width = pw; c.height = ph;
    c.getContext('2d').drawImage(sourceCanvas, 0, 0, pw, ph);
    previewSourceRef.current = c;
  }, [sourceCanvas]);

  const cropPreviewUrl = useMemo(() => {
    if (phase !== 'crop' || !previewSourceRef.current) return '';
    const c = renderProcessed(previewSourceRef.current, crop, mode);
    return c.toDataURL('image/jpeg', 0.85);
    // Recompute whenever mode/crop/source changes.
  }, [phase, mode, crop.x, crop.y, crop.w, crop.h, sourceCanvas]); // eslint-disable-line

  // ─────────── Interactive crop rectangle (pointer-driven) ───────────
  const gestureRef = useRef(null);

  const beginGesture = (kind) => (ev) => {
    ev.preventDefault(); ev.stopPropagation();
    const el = cropContainerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    ev.target.setPointerCapture && ev.target.setPointerCapture(ev.pointerId);
    gestureRef.current = { kind, startX: ev.clientX, startY: ev.clientY,
                           startCrop: { ...crop }, W: rect.width, H: rect.height };
  };

  const onPointerMove = (ev) => {
    const g = gestureRef.current;
    if (!g) return;
    const dxN = (ev.clientX - g.startX) / g.W;
    const dyN = (ev.clientY - g.startY) / g.H;
    const c = { ...g.startCrop };
    const clamp01 = (v) => Math.max(0, Math.min(1, v));
    const MIN = 0.05;
    if (g.kind === 'move') {
      c.x = clamp01(c.x + dxN); c.y = clamp01(c.y + dyN);
      if (c.x + c.w > 1) c.x = 1 - c.w;
      if (c.y + c.h > 1) c.y = 1 - c.h;
    } else {
      const has = (ch) => g.kind.indexOf(ch) !== -1;
      if (has('w')) { const nx = clamp01(c.x + dxN); const nw = c.x + c.w - nx; if (nw >= MIN) { c.x = nx; c.w = nw; } }
      if (has('n')) { const ny = clamp01(c.y + dyN); const nh = c.y + c.h - ny; if (nh >= MIN) { c.y = ny; c.h = nh; } }
      if (has('e')) { const nw = clamp01(c.x + c.w + dxN) - c.x; if (nw >= MIN) c.w = nw; }
      if (has('s')) { const nh = clamp01(c.y + c.h + dyN) - c.y; if (nh >= MIN) c.h = nh; }
    }
    setCrop(c);
  };

  const endGesture = () => { gestureRef.current = null; };

  const close = () => { stopCamera(); onCancel(); };

  if (!open) return null;

  const cssBox = { left: `${crop.x*100}%`, top: `${crop.y*100}%`, width: `${crop.w*100}%`, height: `${crop.h*100}%` };

  return (
    <div data-testid="doc-scanner-modal"
         className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3">
      <div className="bg-white rounded-xl w-full max-w-[820px] max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E9F0]">
          <div className="text-sm font-semibold text-[#11182B]">Scan document{docName ? ` — ${docName}` : ''}</div>
          <button data-testid="doc-scanner-close" onClick={close} className="text-[#5A6377] hover:text-[#11182B]"><X className="w-5 h-5" /></button>
        </div>

        {phase === 'capture' && (
          <div data-testid="doc-scanner-capture-step" className="flex-1 overflow-auto p-4 flex flex-col items-center gap-3 bg-[#0B0F17]">
            <div className="relative w-full max-w-[640px] aspect-video bg-black rounded-md overflow-hidden">
              <video ref={videoRef} muted playsInline className="w-full h-full object-contain" />
              {!cameraReady && (
                <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
                  {cameraErr || 'Waiting for camera…'}
                </div>
              )}
            </div>
            {cameraErr && (
              <div className="w-full max-w-[640px] flex items-start gap-2 text-[12px] text-[#F6C453] bg-[#3A2A0A] border border-[#7A5B1A] rounded-md px-3 py-2">
                <AlertTriangle className="w-4 h-4 mt-[1px] flex-shrink-0" /><span>{cameraErr}</span>
              </div>
            )}
            <div className="w-full max-w-[640px] flex flex-wrap items-center justify-center gap-2 pt-1">
              <input ref={galleryInputRef} data-testid="doc-scanner-gallery-input"
                     type="file" accept="image/*" className="hidden" onChange={onGalleryPick} />
              <Button data-testid="doc-scanner-gallery-btn" variant="outline" size="sm"
                      onClick={() => galleryInputRef.current && galleryInputRef.current.click()}>
                <ImageIcon className="w-4 h-4 mr-1" /> Choose from gallery
              </Button>
              <label data-testid="doc-scanner-fallback-input-label" className="text-xs text-[#98A1B2] cursor-pointer">
                <input data-testid="doc-scanner-fallback-input" type="file" accept="image/*" capture="environment"
                       className="hidden" onChange={onGalleryPick} />
                <span className="underline">Use device camera (fallback)</span>
              </label>
            </div>
          </div>
        )}

        {phase === 'crop' && sourceCanvas && (
          <div data-testid="doc-scanner-crop-step" className="flex-1 overflow-auto p-4 flex flex-col items-center gap-3 bg-[#0B0F17]">
            <div ref={cropContainerRef}
                 className="relative bg-black rounded-md overflow-hidden select-none touch-none"
                 style={{ width: '100%', maxWidth: '680px',
                          aspectRatio: `${sourceCanvas.width} / ${sourceCanvas.height}` }}>
              {/* The image the user sees IS the processed image, so the mode
                  selector previews itself in the exact area they're cropping. */}
              <img ref={previewImgRef} alt="captured document"
                   src={cropPreviewUrl}
                   className="absolute inset-0 w-full h-full object-fill"
                   draggable={false} />
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute bg-black/50" style={{ left:0, top:0, right:0, height:`${crop.y*100}%` }} />
                <div className="absolute bg-black/50" style={{ left:0, bottom:0, right:0, height:`${(1-crop.y-crop.h)*100}%` }} />
                <div className="absolute bg-black/50" style={{ left:0, width:`${crop.x*100}%`, top:`${crop.y*100}%`, height:`${crop.h*100}%` }} />
                <div className="absolute bg-black/50" style={{ right:0, width:`${(1-crop.x-crop.w)*100}%`, top:`${crop.y*100}%`, height:`${crop.h*100}%` }} />
              </div>
              <div data-testid="doc-scanner-crop-rect"
                   className="absolute border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.6)] cursor-move"
                   style={cssBox}
                   onPointerDown={beginGesture('move')} onPointerMove={onPointerMove}
                   onPointerUp={endGesture} onPointerCancel={endGesture}>
                {['nw','n','ne','e','se','s','sw','w'].map((k) => {
                  const map = {
                    nw:{left:'-6px',top:'-6px',cursor:'nwse-resize'},
                    n :{left:'50%', top:'-6px',transform:'translateX(-50%)',cursor:'ns-resize'},
                    ne:{right:'-6px',top:'-6px',cursor:'nesw-resize'},
                    e :{right:'-6px',top:'50%',transform:'translateY(-50%)',cursor:'ew-resize'},
                    se:{right:'-6px',bottom:'-6px',cursor:'nwse-resize'},
                    s :{left:'50%', bottom:'-6px',transform:'translateX(-50%)',cursor:'ns-resize'},
                    sw:{left:'-6px',bottom:'-6px',cursor:'nesw-resize'},
                    w :{left:'-6px',top:'50%',transform:'translateY(-50%)',cursor:'ew-resize'},
                  };
                  return (
                    <div key={k} data-testid={`doc-scanner-handle-${k}`}
                         className="absolute w-3 h-3 bg-white border border-black"
                         style={map[k]}
                         onPointerDown={beginGesture(k)} onPointerMove={onPointerMove}
                         onPointerUp={endGesture} onPointerCancel={endGesture} />
                  );
                })}
              </div>
            </div>
            <div className="w-full max-w-[680px] flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Button data-testid="doc-scanner-rotate-btn" variant="outline" size="sm" onClick={rotate}>
                  <RotateCw className="w-4 h-4 mr-1" /> Rotate 90°
                </Button>
              </div>
              <div data-testid="doc-scanner-mode-selector" className="flex items-center gap-1 bg-white/10 rounded-md p-0.5">
                {MODES.map((m) => (
                  <button key={m.key} type="button"
                          data-testid={`doc-scanner-mode-${m.key}`}
                          onClick={() => setMode(m.key)}
                          className={`text-xs px-2.5 py-1 rounded ${mode === m.key ? 'bg-white text-[#11182B]' : 'text-white/80 hover:text-white'}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {phase === 'review' && reviewDataUrl && (
          <div data-testid="doc-scanner-review-step" className="flex-1 overflow-auto p-4 flex flex-col items-center gap-3 bg-[#0B0F17]">
            <div className="text-[11px] text-[#98A1B2] uppercase tracking-wider">Review — this is exactly what will be uploaded</div>
            <div className="w-full max-w-[720px] bg-white rounded-md overflow-hidden flex items-center justify-center">
              <img ref={reviewImgRef}
                   data-testid="doc-scanner-review-image"
                   src={reviewDataUrl}
                   alt="processed scan preview"
                   className="max-w-full h-auto" />
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="doc-scanner-view-btn" variant="outline" size="sm" onClick={viewFullSize}>
                <Eye className="w-4 h-4 mr-1" /> View full size
              </Button>
            </div>
          </div>
        )}

        <div className="px-4 py-3 border-t border-[#E6E9F0] flex items-center justify-end gap-2 bg-white">
          <Button data-testid="doc-scanner-cancel" variant="outline" size="sm" onClick={close}>Cancel</Button>
          {phase === 'capture' && (
            <Button data-testid="doc-scanner-capture-btn" size="sm"
                    disabled={!cameraReady} onClick={captureFromVideo}>
              <CameraIcon className="w-4 h-4 mr-1" /> Capture
            </Button>
          )}
          {phase === 'crop' && (
            <>
              <Button data-testid="doc-scanner-retake-btn" variant="outline" size="sm" onClick={backToCapture}>
                <RotateCcw className="w-4 h-4 mr-1" /> Retake
              </Button>
              <Button data-testid="doc-scanner-use-btn" size="sm" onClick={goToReview}>
                <Check className="w-4 h-4 mr-1" /> Crop &amp; use
              </Button>
            </>
          )}
          {phase === 'review' && (
            <>
              <Button data-testid="doc-scanner-back-btn" variant="outline" size="sm" onClick={backToCrop}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button data-testid="doc-scanner-upload-btn" size="sm" onClick={upload}>
                <Check className="w-4 h-4 mr-1" /> Upload
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
